import React, { useState, useEffect, Suspense, lazy } from 'react';
// Optimized MUI imports for tree shaking
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './components/Layout/AdminLayout';
import LoginPage from './pages/LoginPage';
import ClaimAccountPage from './pages/ClaimAccountPage';
import Dashboard from './pages/Dashboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastContainer } from './components/Shared/ToastContainer';

// Lazy load heavy components
const ApiProvidersPage = lazy(() => import('./pages/ApiProviders'));
const GenerationResults = lazy(() => import('./pages/GenerationResults'));
const VideoInputRules = lazy(() => import('./pages/VideoInputRules'));
const MenuManagement = lazy(() => import('./pages/MenuManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const BotMessagesManagement = lazy(() => import('./pages/BotMessagesManagement'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ReferralSettings = lazy(() => import('./pages/ReferralSettings'));
const LegalDocuments = lazy(() => import('./pages/LegalDocuments'));
const PdnActs = lazy(() => import('./pages/PdnActs'));
const FileManager = lazy(() => import('./components/FileManager'));
const UserSessionManager = lazy(() => import('./components/UserSessionManager'));
const ContextMessagesPage = lazy(() => import('./pages/ContextMessagesPage'));
const ReferenceMaterialsPage = lazy(() => import('./pages/ReferenceMaterialsPage'));
const TaskLifecycleMonitoring = lazy(() => import('./pages/TaskLifecycleMonitoring'));
const CostReferenceManagement = lazy(() => import('./pages/CostReferenceManagement'));
const ToapisPurchasePrices = lazy(() => import('./pages/ToapisPurchasePrices'));
const GooglePurchasePrices = lazy(() => import('./pages/GooglePurchasePrices'));
const KiePurchasePrices = lazy(() => import('./pages/KiePurchasePrices'));
const RknSettings = lazy(() => import('./pages/RknSettings'));
const Limits = lazy(() => import('./pages/Limits'));
const Mcp = lazy(() => import('./pages/Mcp'));
const SpecialFeaturesPage = lazy(() => import('./pages/SpecialFeaturesPage'));
const ZavodPage = lazy(() => import('./pages/ZavodPage'));
const ParkingMode = lazy(() => import('./pages/ParkingMode'));
const StorageManagement = lazy(() => import('./pages/StorageManagement'));
const StorageMetering = lazy(() => import('./pages/StorageMetering'));
const BackupManagement = lazy(() => import('./pages/BackupManagement'));
const ProviderAlerts = lazy(() => import('./pages/ProviderAlerts'));
const CreditPackagesManagement = lazy(() => import('./pages/CreditPackagesManagement'));
const LegalEntitiesPage = lazy(() => import('./pages/LegalEntitiesPage'));
const WebServicesPage = lazy(() => import('./pages/WebServicesPage'));
const TgServicesPage = lazy(() => import('./pages/TgServicesPage'));
const NewsManagement = lazy(() => import('./pages/NewsManagement'));
const GpuOnDemandManagement = lazy(() => import('./pages/GpuOnDemandManagement'));
const PosterSubscriptions = lazy(() => import('./pages/PosterSubscriptions'));
const Arbitration = lazy(() => import('./pages/Arbitration'));

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
});

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
      >
        Loading...
      </Box>
    );
  }

  // Если не аутентифицированы — доступен только /login и /claim-account, остальные пути редиректим
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/claim-account" element={<ClaimAccountPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Аутентифицированным — основная админка; переход на /login редиректит на /dashboard
  return (
    <AdminLayout>
      <Suspense fallback={
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      }>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/menu" element={<MenuManagement />} />
          <Route path="/bot-messages" element={<BotMessagesManagement />} />
          <Route path="/context-messages" element={<ContextMessagesPage />} />
          <Route path="/reference-materials" element={<ReferenceMaterialsPage />} />
          <Route path="/api-providers" element={<ApiProvidersPage />} />
          <Route path="/cost-reference" element={<CostReferenceManagement />} />
          <Route path="/toapis-purchase-prices" element={<ToapisPurchasePrices />} />
          <Route path="/google-purchase-prices" element={<GooglePurchasePrices />} />
          <Route path="/kie-purchase-prices" element={<KiePurchasePrices />} />
          <Route path="/rkn" element={<RknSettings />} />
          <Route path="/limits" element={<Limits />} />
          <Route path="/mcp" element={<Mcp />} />
          <Route path="/special-features" element={<SpecialFeaturesPage />} />
          <Route path="/zavod" element={<ZavodPage />} />
          <Route path="/runpod-gpu" element={<Navigate to="/gpu-pricing?provider=runpod" replace />} />
          <Route path="/gpu-pricing" element={<GpuOnDemandManagement />} />
          <Route path="/generation-results" element={<GenerationResults />} />
          <Route path="/lifecycle-monitoring" element={<TaskLifecycleMonitoring />} />
          <Route path="/video-input-rules" element={<VideoInputRules />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/files" element={<FileManager />} />
          <Route path="/user-sessions" element={<UserSessionManager />} />
          <Route path="/parking" element={<ParkingMode />} />
          <Route path="/storage" element={<StorageManagement />} />
          <Route path="/storage-metering" element={<StorageMetering />} />
          <Route path="/backups" element={<BackupManagement />} />
          <Route path="/provider-alerts" element={<ProviderAlerts />} />
          <Route path="/credit-packages" element={<CreditPackagesManagement />} />
          <Route path="/legal-entities" element={<LegalEntitiesPage />} />
          <Route path="/web-services" element={<WebServicesPage />} />
          <Route path="/tg-services" element={<TgServicesPage />} />
          <Route path="/news" element={<NewsManagement />} />
          <Route path="/poster-subscriptions" element={<PosterSubscriptions />} />
          <Route path="/arbitration" element={<Arbitration />} />
          <Route path="/referral" element={<ReferralSettings />} />
          <Route path="/legal-documents" element={<LegalDocuments />} />
          <Route path="/pdn-acts" element={<PdnActs />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </AdminLayout>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AppRoutes />
        </Router>
        <ToastContainer />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
